import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Timeout ceilings — keep these generous enough to survive a slow
// network but tight enough that a stuck request surfaces a recovery UI
// instead of a blank "AUTHENTICATING…" screen going live in production.
const SESSION_EXCHANGE_TIMEOUT_MS = 15_000;   // /auth/session hard cap
const WATCHDOG_STUCK_UI_MS = 12_000;          // show retry UI after 12s

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, setLoading } = useAuth();
  const hasProcessed = useRef(false);
  const [stuck, setStuck] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Watchdog: if the async chain hasn't set a user or navigated within
    // WATCHDOG_STUCK_UI_MS, flip the UI into a recovery state so the
    // user always has a way out (never a permanent blank screen).
    const watchdog = setTimeout(() => setStuck(true), WATCHDOG_STUCK_UI_MS);

    const finalize = () => clearTimeout(watchdog);

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      finalize();
      setLoading(false);
      navigate("/login", { replace: true });
      return;
    }
    const session_id = m[1];

    (async () => {
      try {
        const ref = localStorage.getItem("bfa_ref_code") || undefined;
        let remember = true;
        try { remember = sessionStorage.getItem("bf_login_remember") !== "0"; } catch { /* noop */ }

        const res = await api.post(
          "/auth/session",
          { session_id, ref, remember },
          { timeout: SESSION_EXCHANGE_TIMEOUT_MS },
        );

        localStorage.removeItem("bfa_ref_code");
        try { sessionStorage.removeItem("bf_login_remember"); } catch { /* noop */ }
        setUser(res.data.user);
        setLoading(false);
        window.history.replaceState(null, "", window.location.pathname);

        let dest = res.data.user?.station_id ? "/dashboard" : "/onboarding";
        try {
          const next = sessionStorage.getItem("bf_post_login_next");
          if (next && next.startsWith("/")) {
            dest = next;
            sessionStorage.removeItem("bf_post_login_next");
          }
        } catch { /* storage blocked */ }
        finalize();
        navigate(dest, { replace: true, state: { user: res.data.user } });
      } catch (e) {
        // Emergent session_ids are SINGLE-USE — if the OAuth callback URL
        // gets opened twice (page refresh / StrictMode double-mount / stale
        // tab), the first call consumes the session_id and the second
        // returns 401. Before we bounce the user to /login, check whether
        // the FIRST call already set a session_token cookie; if so, drop
        // the user straight into the dashboard.
        try {
          const me = await api.get("/auth/me", { timeout: 8_000 });
          if (me?.data?.user_id) {
            setUser(me.data);
            setLoading(false);
            window.history.replaceState(null, "", window.location.pathname);
            let dest = me.data?.station_id ? "/dashboard" : "/onboarding";
            try {
              const next = sessionStorage.getItem("bf_post_login_next");
              if (next && next.startsWith("/")) {
                dest = next;
                sessionStorage.removeItem("bf_post_login_next");
              }
            } catch { /* storage blocked */ }
            finalize();
            navigate(dest, { replace: true, state: { user: me.data } });
            return;
          }
        } catch { /* fall through — no valid cookie either */ }

        // Genuine failure — surface a friendly message and unstick the
        // loading state so ProtectedRoute doesn't spin forever.
        // eslint-disable-next-line no-console
        console.error("auth exchange failed", e);
        const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
        setErrorMsg(
          isTimeout
            ? "Sign-in is taking longer than usual. Check your connection and try again."
            : "We couldn't complete sign-in. Please try again.",
        );
        setStuck(true);
        setLoading(false);
        finalize();
        // Deliberately do NOT auto-navigate — let the user click the
        // recovery button so they see what happened instead of a silent
        // bounce back to /login.
      }
    })();

    // Cleanup: cancel the watchdog on unmount so a fast success doesn't
    // leak a timer into the next mount.
    return finalize;
  }, [navigate, setUser, setLoading]);

  if (stuck) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg px-6">
        <div className="text-center max-w-sm" data-testid="auth-callback-stuck">
          <div className="text-brand-amber font-mono text-xs uppercase tracking-wider mb-3">
            // Sign-in issue
          </div>
          <p className="text-ink-primary text-sm mb-6">
            {errorMsg || "Sign-in is taking longer than usual."}
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                window.history.replaceState(null, "", "/login");
                window.location.href = "/login";
              }}
              data-testid="auth-callback-retry"
              className="w-full rounded-md bg-brand-amber px-4 py-2.5 text-black font-semibold text-sm hover:bg-brand-amber/90 transition"
            >
              Return to sign in
            </button>
            <a
              href="/"
              data-testid="auth-callback-home"
              className="w-full inline-block rounded-md border border-line bg-bg-elevated px-4 py-2.5 text-ink-secondary text-sm hover:text-ink-primary transition"
            >
              Back to homepage
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="text-center">
        <div className="text-brand-amber font-mono text-sm" data-testid="auth-callback-loading">
          AUTHENTICATING...
        </div>
      </div>
    </div>
  );
}
